import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import AppRoutes from 'constants/AppRoutes';
import { KEY_PERMISSIONS, MATERIALITY_ASSESSMENT_STATUS, TASK_MANAGEMENT_PERMISSIONS } from 'constants/general';
import { DEFAULT_COUNTER_COMPETITORS, SECTION_TYPE, STATUS_SECTIONS } from 'constants/materialAssessment';
import useErrorMessage from 'hooks/useErrorMessage';
import usePillarModulePermission from 'hooks/usePillarModulePermission';
import i18n from 'i18n';
import { useUpdateSectionMAMutation } from 'store/slices/layoutsMA/apis/materialAssessmentApi';
import { useDeleteCompetitorMAMutation, useGetSingleMAInitiateQuery, useGetStakeholdersCompetitorsListQuery } from 'store/slices/materialAssessment/apis/materialAssessmentApi';
import { useUserMeQuery } from 'store/slices/user/apis/user';

import MaterialityConfigCompetitorView from './MaterialityConfigCompetitorView';

import type IStakeholderCompetitorItem from 'store/slices/materialAssessment/interfaces/IStakeholderCompetitorItem';

function MaterialityConfigCompetitor() {
  const navigate = useNavigate();
  const { recordId = '', vcmId = 0 } = useParams();
  const [openModal, setOpenModal] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState<number>(-1);
  const [competitors, setCompetitors] = useState<IStakeholderCompetitorItem[]>([]);

  const [deleteCompetitor] = useDeleteCompetitorMAMutation();
  const [updateSection] = useUpdateSectionMAMutation();

  const {
    data: competitorItems,
    isFetching: isCompetitorLoading,
    error: fetchError,
  } = useGetStakeholdersCompetitorsListQuery({
    maId: recordId,
    offset: 0,
    vcmId: Number(vcmId),
  }, {
    skip: vcmId === 0,
  });

  const { data: userData, isLoading: isUserLoading } = useUserMeQuery();
  const { data: singleMA, isFetching: isLoadingSingleMA } = useGetSingleMAInitiateQuery({ id: recordId });

  const {
    access,
  } = usePillarModulePermission({
    pillarModuleKey: KEY_PERMISSIONS.MATERIALITY_ASSESSMENT_MANAGEMENT,
    userId: userData?.data?.id,
  });

  const isViewOnly = access === TASK_MANAGEMENT_PERMISSIONS.VIEW_ONLY
  || access === TASK_MANAGEMENT_PERMISSIONS.SIGN_OFF
  || singleMA?.data?.status === MATERIALITY_ASSESSMENT_STATUS.APPROVED
  || singleMA?.data?.status === MATERIALITY_ASSESSMENT_STATUS.PENDING_REVIEW;

  const errorMessage = useErrorMessage(fetchError);

  useEffect(() => {
    if (errorMessage && errorMessage !== '') {
      toast.error(i18n.t('notifications.errorHandlerTextMessage', { message: errorMessage }));
    }
  }, [errorMessage]);

  useEffect(() => {
    if (competitorItems && competitorItems?.data?.items.length > 0) {
      setCompetitors((prev) => {
        const existingIds = new Set(prev.map((item) => item.companyName));
        const newItems = competitorItems?.data?.items
          .filter((item) => !existingIds.has(item.companyName))
          .map((item) => ({ ...item, custom: true }));
        return [...prev, ...newItems];
      });
    }
  }, [competitorItems]);

  const onOpenModal = () => {
    setOpenModal(!openModal);
  };

  const goBack = () => {
    navigate(AppRoutes.materialityAssessmentTopicIdentification.replace(':recordId', recordId));
  };

  const saveCompetitor = () => {
    if (!isViewOnly) {
      updateSection({
        maId: recordId,
        type: SECTION_TYPE.TOPIC_1_PEER_COMPETITOR_INSIGHTS,
        valueChainMapId: Number(vcmId),
        data: {
          isActive: true,
          status: competitors.length > 0 ? STATUS_SECTIONS.COMPLETED : STATUS_SECTIONS.EMPTY,
        },
      });
    }
    navigate(AppRoutes.materialityAssessmentTopicIdentification.replace(':recordId', recordId));
  };

  const onDeleteItem = (id: number) => {
    setSelectedItem(id);
    setIsDeleteModalOpen(true);
  };

  const onCancel = () => {
    setOpenModal(false);
    setSelectedItem(-1);
    setIsDeleteModalOpen(false);
  };

  const onDeleteConfirm = () => {
    deleteCompetitor({ id: selectedItem, maId: recordId, vcmId: Number(vcmId) }).then(() => {
      toast.success(i18n.t('notifications.deletedCompetitor'));
    }).catch(() => {
      toast.error(i18n.t('notifications.errorDeleteCompetitor'));
    });
    setCompetitors((prev) => prev.filter((item) => item.id !== selectedItem));
    onCancel();
  };

  const loading = isCompetitorLoading || isUserLoading || isLoadingSingleMA;

  return (
    <MaterialityConfigCompetitorView
      items={competitors}
      isEmptyList={competitors.length === 0}
      loading={loading}
      onOpenModal={onOpenModal}
      goBack={goBack}
      saveCompetitor={saveCompetitor}
      openModal={openModal}
      onDeleteItem={onDeleteItem}
      count={competitors.length}
      max={DEFAULT_COUNTER_COMPETITORS}
      isDeleteModalOpen={isDeleteModalOpen}
      onCancel={onCancel}
      onDeleteConfirm={onDeleteConfirm}
      recordId={recordId}
      setOpenModal={setOpenModal}
      vcmId={Number(vcmId)}
      isViewOnly={isViewOnly}
    />
  );
}

export default MaterialityConfigCompetitor;
